import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { completeTour } from '../../api/tourApi';
import { isTourHome, tourSteps, workspaceFor, type TourStep } from './tourSteps';

/** SRS #39 — interactive first-time tour. No library: a spotlight cut-out
 *  over the anchored element and a card with Back / Next / Skip.
 *
 *  Starts once per account (server flag `tour_completed_at`, mirrored in
 *  localStorage so a slow /users/me never replays it) the first time the
 *  reader lands on a dashboard home; can be restarted from the User Guide. */

const DONE_KEY = 'policyora_tour_done';

interface TourApi { active: boolean; start: () => void; stop: () => void; stepIndex: number; steps: TourStep[] }
const TourContext = createContext<TourApi>({ active: false, start: () => {}, stop: () => {}, stepIndex: 0, steps: [] });
export const useTour = () => useContext(TourContext);

const doneLocally = () => { try { return localStorage.getItem(DONE_KEY) === '1'; } catch { return false; } };
const markDone = () => { try { localStorage.setItem(DONE_KEY, '1'); } catch { /* ignore */ } };

function findAnchor(step: TourStep): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${step.anchor}"]`);
  if (el) return el;
  return step.fallback ? document.querySelector<HTMLElement>(step.fallback) : null;
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const steps = useMemo(() => tourSteps(workspaceFor(location.pathname)), [location.pathname]);
  const autoStarted = useRef(false);

  const finish = useCallback(() => {
    setActive(false);
    setRect(null);
    markDone();
    completeTour().catch(() => {});
  }, []);
  const start = useCallback(() => { setStepIndex(0); setRect(null); setActive(true); }, []);

  // Auto-start once for a first-time reader on a dashboard home.
  useEffect(() => {
    if (!user || autoStarted.current || active) return;
    if (user.tour_completed_at || doneLocally()) return;
    if (!isTourHome(location.pathname)) return;
    autoStarted.current = true;
    const t = setTimeout(start, 1200);
    return () => clearTimeout(t);
  }, [user, location.pathname, active, start]);

  // Position the spotlight for the current step; navigate when the step
  // lives elsewhere; skip steps whose anchor never appears.
  useEffect(() => {
    if (!active) return;
    const step = steps[stepIndex];
    if (!step) { finish(); return; }
    if (!location.pathname.startsWith(step.route)) { navigate(step.route); return; }
    let tries = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const el = findAnchor(step);
      if (el) {
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView();
        setRect(el.getBoundingClientRect());
        return;
      }
      if (++tries < 15) { setTimeout(tick, 150); return; }
      // Anchor never rendered (locked page, collapsed nav) — move on.
      setStepIndex(i => i + 1);
    };
    tick();
    const onResize = () => { const el = findAnchor(step); if (el) setRect(el.getBoundingClientRect()); };
    window.addEventListener('resize', onResize);
    return () => { cancelled = true; window.removeEventListener('resize', onResize); };
  }, [active, stepIndex, steps, location.pathname, navigate, finish]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') finish(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, finish]);

  const api = useMemo<TourApi>(() => ({ active, start, stop: finish, stepIndex, steps }), [active, start, finish, stepIndex, steps]);
  const step = steps[stepIndex];
  const last = stepIndex >= steps.length - 1;

  return (
    <TourContext.Provider value={api}>
      {children}
      {active && step && rect && ReactDOM.createPortal(
        <div data-testid="guided-tour" role="dialog" aria-modal="true" aria-label={`Tour step ${stepIndex + 1} of ${steps.length}: ${step.title}`}>
          {/* spotlight: a huge box-shadow leaves the anchor uncovered */}
          <div style={{ position: 'fixed', zIndex: 3000, top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, borderRadius: 10, boxShadow: '0 0 0 9999px rgba(16,24,40,0.55), 0 0 0 3px var(--ds-brand-blue)', pointerEvents: 'none' }} />
          <div onClick={finish} style={{ position: 'fixed', inset: 0, zIndex: 2999 }} aria-hidden />
          <div style={{
            position: 'fixed', zIndex: 3001, width: 320, maxWidth: 'calc(100vw - 32px)',
            top: Math.min(window.innerHeight - 220, rect.bottom + 12),
            left: Math.max(16, Math.min(window.innerWidth - 336, rect.left)),
            background: 'var(--ds-surface)', color: 'var(--ds-text)', borderRadius: 12, padding: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ds-link)', marginBottom: 4 }}>
              Step {stepIndex + 1} of {steps.length}
            </div>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>{step.title}</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ds-text-secondary)' }}>{step.body}</div>
            <div style={{ display: 'flex', gap: 4, margin: '12px 0' }} aria-hidden>
              {steps.map((s, i) => <span key={s.id} style={{ width: 8, height: 8, borderRadius: '50%', background: i === stepIndex ? 'var(--ds-brand-blue)' : 'var(--ds-border-strong)' }} />)}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={finish} style={{ background: 'transparent', border: 'none', color: 'var(--ds-text-muted)', cursor: 'pointer', fontSize: 13 }}>Skip tour</button>
              <span style={{ flex: 1 }} />
              {stepIndex > 0 && <button type="button" onClick={() => setStepIndex(i => i - 1)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)', cursor: 'pointer', fontSize: 13 }}>Back</button>}
              <button type="button" onClick={() => (last ? finish() : setStepIndex(i => i + 1))} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                {last ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </TourContext.Provider>
  );
};
